/**
 * 3E Entertainment — API Service Layer
 *
 * Centralized API client for all frontend-to-backend communication.
 * Handles base URL configuration, error handling, and response parsing.
 */

import { normalizeUrl } from '../utils/url';

// In development, API is at localhost; in production, same domain under /api/
const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch(endpoint, options = {}) {
    const url = `${API_BASE}/${endpoint}`;

    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
        ...options,
    };

    // Don't set Content-Type for FormData (file uploads)
    if (options.body instanceof FormData) {
        delete config.headers['Content-Type'];
    }

    try {
        const response = await fetch(url, config);

        // Handle CSV downloads
        if (response.headers.get('content-type')?.includes('text/csv')) {
            return response.blob();
        }

        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch {
            console.error(`API Error [${endpoint}]: non-JSON response`, text.slice(0, 200));
            throw new Error('Unable to reach the server. Please try again later.');
        }

        if (!response.ok) {
            throw new Error(data.error || `API error: ${response.status}`);
        }

        return data;
    } catch (error) {
        console.error(`API Error [${endpoint}]:`, error.message);
        throw error;
    }
}

// ─── Events API ─────────────────────────────────────────────────────────

/**
 * Get events filtered by status
 * @param {'upcoming'|'past'|'ongoing'|'all'} status
 * @param {Object} options - { category, featured, heroHighlighted, limit, offset }
 */
export async function getEvents(status = 'all', options = {}) {
    const params = new URLSearchParams({ status });
    if (options.category) params.set('category', options.category);
    if (options.featured) params.set('featured', '1');
    if (options.heroHighlighted) params.set('hero_highlighted', '1');
    if (options.limit) params.set('limit', options.limit);
    if (options.offset) params.set('offset', options.offset);

    return apiFetch(`events.php?${params}`);
}

/**
 * Get a single event by slug
 */
export async function getEventBySlug(slug) {
    return apiFetch(`events.php?slug=${encodeURIComponent(slug)}`);
}

/**
 * Get 3E supported events
 * @param {'upcoming'|'past'|'ongoing'|'all'} status
 * @param {Object} options - { category, featured, limit, offset }
 */
export async function getSupportedEvents(status = 'all', options = {}) {
    const params = new URLSearchParams({ status });
    if (options.category) params.set('category', options.category);
    if (options.featured) params.set('featured', '1');
    if (options.limit) params.set('limit', options.limit);
    if (options.offset) params.set('offset', options.offset);

    return apiFetch(`supported-events.php?${params}`);
}

/**
 * Get active annual sponsors
 */
export async function getAnnualSponsors() {
    return apiFetch('annual-sponsors.php');
}

// ─── Gallery API ────────────────────────────────────────────────────────

/**
 * Get gallery images filtered by section
 * @param {'hero'|'portfolio'|'about'|'events'|'gallery'|'general'} section
 * @param {Object} options - { event_id, limit, offset }
 */
export async function getGalleryImages(section = null, options = {}) {
    const params = new URLSearchParams();
    if (section) params.set('section', section);
    if (options.event_id) params.set('event_id', options.event_id);
    if (options.limit) params.set('limit', options.limit);
    if (options.offset) params.set('offset', options.offset);

    const query = params.toString();
    return apiFetch(`gallery.php${query ? '?' + query : ''}`);
}

// ─── Newsletter API ─────────────────────────────────────────────────────

/**
 * Subscribe to newsletter
 * @param {Object} data - { name, email, source }
 */
export async function subscribeNewsletter({ name, email, source = 'popup' }) {
    return apiFetch('newsletter.php', {
        method: 'POST',
        body: JSON.stringify({ name, email, source, consent: true }),
    });
}

// ─── Sponsorships API ───────────────────────────────────────────────────

/**
 * Submit a sponsorship inquiry
 * @param {Object} data - { company_name, contact_name, email, phone, website, tier, tier_price, message }
 */
export async function submitSponsorshipInquiry(data) {
    const payload = {
        ...data,
        website: normalizeUrl(data.website, { fieldName: 'Website', maxLength: 255 }),
    };
    return apiFetch('sponsorships.php', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// ─── Tour Booking API ───────────────────────────────────────────────────

/**
 * Published tours with their line-ups and city schedules, as managed in
 * admin/tours.php. Returns [] when the tours migration has not been run yet,
 * so callers can fall back to the bundled catalogue in src/data/tours.js.
 */
export async function fetchTours() {
    const data = await apiFetch('tours.php');
    return Array.isArray(data.tours) ? data.tours : [];
}

export async function submitTourBooking(data) {
    return apiFetch('tour-bookings.php', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

// ─── API Availability Check ─────────────────────────────────────────────

/**
 * Check if the API is available (for graceful fallback to hardcoded data)
 */
export async function isApiAvailable() {
    try {
        const response = await fetch(`${API_BASE}/events.php?status=all&limit=1`, {
            method: 'GET',
            signal: AbortSignal.timeout(3000), // 3 second timeout
        });
        return response.ok;
    } catch {
        return false;
    }
}
